<script setup lang="ts">
import { computed } from 'vue';
import {
  type ApiImage,
  buildSrcSet,
  isOptimisedImage,
} from '@/types/images';
import { onOfflineMediaImgError } from '@/utils/offlineMediaPlaceholder';

type PersonPreviewPayload = {
  name?: string | null;
  title?: string | null;
  department?: string | null;
  description?: string | null;
  image?: ApiImage;
  layout?: string | null;
};

const props = withDefaults(
  defineProps<{
    name?: string | null;
    title?: string | null;
    department?: string | null;
    description?: string | null;
    image?: ApiImage;
    /** Set by the pages API for the standalone `person` stream block (not stored in Wagtail). */
    layout?: 'featured' | null;
    /** Block-picker iframe: JSON from `get_api_representation`. Query key `payload`. */
    payload?: string | null;
  }>(),
  { layout: null }
);

const previewData = computed((): PersonPreviewPayload | null => {
  const raw = props.payload?.trim();
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as unknown;
    if (p && typeof p === 'object') return p as PersonPreviewPayload;
    return null;
  } catch {
    return null;
  }
});

const resolved = computed(() => {
  const prev = previewData.value;
  if (prev) {
    return {
      name: prev.name,
      title: prev.title,
      department: prev.department,
      description: prev.description,
      image: prev.image,
      layout: (prev.layout as 'featured' | null | undefined) ?? 'featured',
    };
  }
  return {
    name: props.name,
    title: props.title,
    department: props.department,
    description: props.description,
    image: props.image,
    layout: props.layout,
  };
});

const isFeatured = computed(() => resolved.value.layout === 'featured');

const legacyImageUrl = computed(() => {
  const img = resolved.value.image;
  return img && 'url' in img ? img.url : null;
});
</script>

<template>
  <article
    v-if="resolved.name"
    class="person-block"
    :class="{ 'person-block--featured': isFeatured }"
  >
    <!-- Profile image: prominent at top (matches prototype) -->
    <div class="person-block__image-wrap">
      <template v-if="resolved.image">
        <picture v-if="isOptimisedImage(resolved.image)">
          <source
            v-for="source in resolved.image.sources"
            :key="source.media_type"
            :type="source.media_type"
            :srcset="buildSrcSet(source.src_set)"
          />
          <img
            :src="resolved.image.fallback!.url"
            :srcset="buildSrcSet(resolved.image.fallback!.src_set)"
            :alt="resolved.image.title ?? resolved.name ?? ''"
            class="person-block__image"
            loading="lazy"
            @error="onOfflineMediaImgError"
          />
        </picture>
        <img
          v-else-if="legacyImageUrl"
          :src="legacyImageUrl"
          :alt="resolved.name ?? ''"
          class="person-block__image"
          loading="lazy"
          @error="onOfflineMediaImgError"
        />
      </template>
      <div v-else class="person-block__image-placeholder" aria-hidden="true">
        <span class="person-block__image-initial">{{ resolved.name.charAt(0).toUpperCase() }}</span>
      </div>
    </div>

    <div class="person-block__body">
      <!-- Member info: name (primary), title/department (secondary), description (supporting) -->
      <div class="person-block__meta">
        <div class="person-block__name">{{ resolved.name }}</div>
        <p v-if="resolved.title" class="person-block__title">{{ resolved.title }}</p>
        <p v-if="resolved.department" class="person-block__department">{{ resolved.department }}</p>
      </div>
      <p v-if="resolved.description" class="person-block__description">{{ resolved.description }}</p>
    </div>
  </article>
</template>

<style scoped>
.person-block {
  background-color: var(--color-card-bg, #ffffff);
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 0.75rem;
  padding: 1rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: border-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;
}

.person-block:not(.person-block--featured) .person-block__body {
  display: contents;
}

.person-block:not(.person-block--featured):hover {
  border-color: rgba(4, 120, 87, 0.5);
  box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
  transform: translateY(-4px);
}

/* Prominent profile image at top (prototype: w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20) */
.person-block__image-wrap {
  margin-bottom: 0.75rem;
  flex-shrink: 0;
}

.person-block__image {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 0.75rem;
  object-fit: cover;
  display: block;
}

.person-block__image-placeholder {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 0.75rem;
  background-color: rgba(4, 120, 87, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}

.person-block__image-initial {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-link, #047857);
}

/* Primary: name */
.person-block__meta {
  margin-bottom: 0.5rem;
}

.person-block__name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.25rem;
  transition: color 0.3s ease;
}

.person-block:not(.person-block--featured):hover .person-block__name {
  color: var(--color-link, #047857);
}

/* Secondary: title (accent), department (muted) */
.person-block__title {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--color-link, #047857);
  margin: 0 0 0.125rem;
}

.person-block__department {
  font-size: 0.75rem;
  color: var(--color-text-muted, #525252);
  margin: 0;
}

/* Supporting: description */
.person-block__description {
  font-size: 0.75rem;
  line-height: 1.5;
  color: var(--color-text-muted, #525252);
  margin: 0;
  flex: 1;
}

@media (min-width: 640px) {
  .person-block:not(.person-block--featured) {
    border-radius: 1rem;
    padding: 1.25rem;
  }

  .person-block:not(.person-block--featured) .person-block__image-wrap {
    margin-bottom: 1rem;
  }

  .person-block:not(.person-block--featured) .person-block__image {
    width: 4rem;
    height: 4rem;
    border-radius: 1rem;
  }

  .person-block:not(.person-block--featured) .person-block__image-placeholder {
    width: 4rem;
    height: 4rem;
    border-radius: 1rem;
  }

  .person-block:not(.person-block--featured) .person-block__image-initial {
    font-size: 1.5rem;
  }

  .person-block:not(.person-block--featured) .person-block__name {
    font-size: 1rem;
  }

  .person-block:not(.person-block--featured) .person-block__title,
  .person-block:not(.person-block--featured) .person-block__department {
    font-size: 0.8125rem;
  }

  .person-block:not(.person-block--featured) .person-block__description {
    font-size: 0.875rem;
  }
}

@media (min-width: 768px) {
  .person-block:not(.person-block--featured) {
    padding: 1.5rem;
  }

  .person-block:not(.person-block--featured) .person-block__image {
    width: 5rem;
    height: 5rem;
    border-radius: 1rem;
  }

  .person-block:not(.person-block--featured) .person-block__image-placeholder {
    width: 5rem;
    height: 5rem;
    border-radius: 1rem;
  }

  .person-block:not(.person-block--featured) .person-block__image-initial {
    font-size: 1.75rem;
  }

  .person-block:not(.person-block--featured) .person-block__name {
    font-size: 1.125rem;
  }

  .person-block:not(.person-block--featured) .person-block__meta {
    margin-bottom: 0.75rem;
  }
}

/* Featured: full-width card + horizontal layout (prototype director profile) */
.person-block--featured {
  flex-direction: column;
  align-items: stretch;
  gap: 1rem;
  background-color: var(--color-card-bg, #ffffff);
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 0.375rem;
  padding: 1rem;
  height: auto;
  box-shadow: none;
}

@media (min-width: 640px) {
  .person-block--featured {
    padding: 1.5rem;
  }
}

@media (min-width: 768px) {
  .person-block--featured {
    flex-direction: row;
    align-items: flex-start;
    gap: 2rem;
    padding: 2rem;
  }
}

.person-block--featured .person-block__image-wrap {
  margin: 0 auto 0;
  flex-shrink: 0;
}

@media (min-width: 768px) {
  .person-block--featured .person-block__image-wrap {
    margin: 0;
  }
}

.person-block--featured .person-block__image,
.person-block--featured .person-block__image-placeholder {
  width: 6rem;
  height: 6rem;
  border-radius: 0.375rem;
  border: 1px solid var(--color-border, #e5e7eb);
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
}

.person-block--featured .person-block__image-placeholder {
  box-shadow: none;
}

@media (min-width: 640px) {
  .person-block--featured .person-block__image,
  .person-block--featured .person-block__image-placeholder {
    width: 8rem;
    height: 8rem;
  }
}

@media (min-width: 768px) {
  .person-block--featured .person-block__image,
  .person-block--featured .person-block__image-placeholder {
    width: 12rem;
    height: 12rem;
  }
}

.person-block--featured .person-block__body {
  flex: 1;
  min-width: 0;
  text-align: center;
}

@media (min-width: 768px) {
  .person-block--featured .person-block__body {
    text-align: left;
  }
}

.person-block--featured .person-block__meta {
  margin-bottom: 1rem;
}

.person-block--featured .person-block__name {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.25rem;
}

@media (min-width: 640px) {
  .person-block--featured .person-block__name {
    font-size: 1.25rem;
  }
}

@media (min-width: 768px) {
  .person-block--featured .person-block__name {
    font-size: 1.5rem;
  }
}

.person-block--featured .person-block__title {
  font-size: 0.875rem;
  font-weight: 500;
  margin: 0 0 0.25rem;
}

@media (min-width: 768px) {
  .person-block--featured .person-block__title {
    font-size: 1.125rem;
  }
}

.person-block--featured .person-block__department {
  font-size: 0.75rem;
  margin: 0;
}

@media (min-width: 640px) {
  .person-block--featured .person-block__department {
    font-size: 0.875rem;
  }
}

.person-block--featured .person-block__description {
  font-size: 0.75rem;
  line-height: 1.625;
  margin: 0;
}

@media (min-width: 640px) {
  .person-block--featured .person-block__description {
    font-size: 0.875rem;
  }
}

@media (min-width: 768px) {
  .person-block--featured .person-block__description {
    font-size: 1rem;
  }
}
</style>
